import { useCallback, useMemo } from 'react';
import { useQueryState } from 'nuqs';
import type { IntegrationFilters } from './types';

/**
 * The directory's five URL-backed filter params. Empty values serialize to
 * `null` (dropped from the URL) rather than `?query=`, and leaving the route
 * drops them entirely, so the filters reset on exit.
 *
 * `query`, `selectedCategory`, `selectedType`, and `selectedConnectionType`
 * drive the filtered list; `selectedGroup` is a declared passthrough with no
 * consumer (kept so its deep links round-trip through the URL).
 */
export type DirectoryUrlState = {
  filters: IntegrationFilters;
  query: string;
  setQuery: (value: string) => void;
  clearQuery: () => void;
  selectedType: string;
  setSelectedType: (value: string) => void;
  selectedCategory: string;
  setSelectedCategory: (value: string) => void;
};

function emptyToNull(value: string): string | null {
  return value === '' ? null : value;
}

export function useDirectoryUrlState(): DirectoryUrlState {
  const [query, setQueryParam] = useQueryState('query');
  const [selectedCategory, setSelectedCategoryParam] = useQueryState('selectedCategory');
  const [selectedType, setSelectedTypeParam] = useQueryState('selectedType');
  const [selectedConnectionType] = useQueryState('selectedConnectionType');
  // Declared so its deep links round-trip and reset on exit; no filter consumes it.
  useQueryState('selectedGroup');

  const setQuery = useCallback(
    (value: string) => {
      void setQueryParam(emptyToNull(value));
    },
    [setQueryParam],
  );

  const clearQuery = useCallback(() => {
    void setQueryParam(null);
  }, [setQueryParam]);

  const setSelectedType = useCallback(
    (value: string) => {
      void setSelectedTypeParam(emptyToNull(value));
    },
    [setSelectedTypeParam],
  );

  const setSelectedCategory = useCallback(
    (value: string) => {
      void setSelectedCategoryParam(emptyToNull(value));
    },
    [setSelectedCategoryParam],
  );

  const filters = useMemo<IntegrationFilters>(
    () => ({
      query: query ?? '',
      selectedCategory: selectedCategory ?? '',
      selectedType: selectedType ?? '',
      selectedConnectionType: selectedConnectionType ?? '',
    }),
    [query, selectedCategory, selectedType, selectedConnectionType],
  );

  return {
    filters,
    query: query ?? '',
    setQuery,
    clearQuery,
    selectedType: selectedType ?? '',
    setSelectedType,
    selectedCategory: selectedCategory ?? '',
    setSelectedCategory,
  };
}
